import {observer} from 'mobx-react-lite';
import React, {useEffect, useState} from 'react';
import {useService} from '../../../../infrastructure/service/service';
import {Title} from '../../../../entity/model/title';
import {DocumentRunnerProps} from '../../../../entity/props/title/document-runner-props';
import Box from '@mui/material/Box';
import {
  Button,
  Card,
  CardContent,
  InputLabel,
  LinearProgress,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LoadingComponent from '../widget/LoadingComponent';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {PageOption} from '../../../../entity/enums/page-option';
import {Form, Formik, FormikProps} from 'formik';
import {DocumentRunnerSearchDto} from '../../../../entity/dto/document-runner-search-dto';
import {County} from '../../../../entity/model/county';
import {DocumentRunnerConfig} from '../../../../entity/resource/scraper-config-resource';
import DocumentRunnerStatusDialog from './DocumentRunnerStatusDialog';
import {toast} from 'react-toastify';
import RefreshIcon from '@mui/icons-material/Refresh';

import '../../../../assets/css/style.css';
import '../../../../assets/css/title/document-runner.css';

const DocumentRunner = (props: DocumentRunnerProps): JSX.Element => {
  const {countyService, titleService, documentService, scraperConfigService, auditLogsService} = useService();

  const [title, setTitle] = useState<Title | undefined>(titleService.getTitle());
  const [loading, setLoading] = useState<boolean>(false);
  const [trigger, setTrigger] = useState<boolean>(false);
  const [orderCounty, setOrderCounty] = useState<County>();
  const [progress, setProgress] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const [enableDocumentRunner, setEnableDocumentRunner] = useState<boolean>(false);
  const [documentRunnerConfig, setDocumentRunnerConfig] = useState<DocumentRunnerConfig>();
  const [openStatusDialog, setOpenStatusDialog] = useState<boolean>(false);
  const [progressRefresh, setProgressRefresh] = useState<boolean>(false);
  const [batchId, setBatchId] = useState<string>('');
  const [lastStateMessage, setLastStateMessage] = useState<string>('');

  useEffect(() => {
    setTitle(titleService.getTitle());
  }, [titleService.getTitle()]);

  useEffect(() => {
    const init = async (): Promise<void> => {
      setLoading(true);

      let county = await countyService.getCountyById(title?.countyID ?? '');
      setOrderCounty(county);

      const scraperConfig = await scraperConfigService.getScraperConfig(title?.countyID ?? '');

      const configValue = scraperConfig?.value;
      if (configValue) {
        setEnableDocumentRunner(configValue.status);
        setDocumentRunnerConfig(configValue);
      } else
        setEnableDocumentRunner(false);

      setLoading(false);
    }

    setProgress(0);
    setMessage('Your request is being processed');

    init();
  }, []);

  const handleSubmit = async (values: DocumentRunnerSearchDto): Promise<void> => {
    setLoading(true);
    setTrigger(true);

    const batchId = crypto.randomUUID();
    await documentService.documentRunner(props.projectTitle.createdByOrganizationID, values.tmsNumber, batchId, documentRunnerConfig, title);

    setLoading(false);
    await fetchProgress(batchId);
  }

  const fetchProgress = async (batchId: string): Promise<void> => {
    let previousProgress = 0;
    let progressUnchangedDuration = 0;
    const maxUnchangedDuration = 20000;
    const interval = 10000;
    const maxUnchangedCycles = maxUnchangedDuration / interval;

    const intervalId = setInterval(async (): Promise<void> => {
      const response = await auditLogsService.getDocumentRunnerLogs(props.projectTitle.createdByOrganizationID, batchId);
      if (response) {
        if (response.progress > progress)
          setProgress(response.progress);

        setMessage(response.message);

        if (response.progress === 90) {
          await titleService.getTitleById(props.projectTitle.createdByOrganizationID, title?.id ?? '');
          setProgress(100);
          setMessage('Document runner execution was successful');
          toast.success('Document runner execution was successful !!');

          setTimeout((): void => titleService.setPageOption(PageOption.DOCUMENTS), 1000);
          clearInterval(intervalId);
        }

        if (response.progress !== previousProgress) {
          previousProgress = response.progress;
          progressUnchangedDuration = 0;
        } else
          progressUnchangedDuration++;

        if (progressUnchangedDuration >= maxUnchangedCycles) {
          setProgressRefresh(true);
          setBatchId(batchId);
          setLastStateMessage(response.message);

          setMessage('Document runner executing. Please refresh to check the progress.');
          toast.info('Document runner executing. Please refresh to check the progress.');
          clearInterval(intervalId);
        }

      } else {
        setTrigger(false);
        clearInterval(intervalId);
      }
    }, interval);
  }

  const handleProgressRefresh = async (): Promise<void> => {
    setProgressRefresh(false);
    setMessage(lastStateMessage);
    setProgress((prevState: number): number => prevState + 1);

    await fetchProgress(batchId);
  }

  return (
    <>
      <Box className={'body-box'}>
        <Formik
          initialValues={documentService.getDocumentRunnerSearchDto(props.projectTitle.tmsParcelNumbers)}
          onSubmit={handleSubmit}>
          {({values, handleChange, handleSubmit}: FormikProps<DocumentRunnerSearchDto>): JSX.Element => (
            <Form onSubmit={handleSubmit}>
              <Box className={'search-online-container'}>
                <Typography className={'document-tab-title'}>
                  Document Runner
                  <span onClick={(): void => setOpenStatusDialog(true)}>Status</span>
                </Typography>
                <InputLabel className={'search-online-input-label'}>TMS / Parcel Number(s)</InputLabel>
                <TextField
                  required
                  label='TMS / Parcel Numbers(s)'
                  name='tmsNumber'
                  value={values.tmsNumber}
                  onChange={handleChange}/>
                <Button
                  type='submit'
                  disableElevation
                  className={`zitles-button icon ${!enableDocumentRunner ? 'disable' : ''}`}
                  variant='contained'
                  disabled={!enableDocumentRunner}>
                  <SearchIcon/>
                  Search on {orderCounty?.name} County
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
        <Card elevation={0} className={'search-online-card'}>
          <CardContent>
            {
              loading
                ? <LoadingComponent/>
                : trigger &&
                  <Box className={'progress-zone'}>
                    <Box className={'progress-icon-zone'}>
                      {
                        progressRefresh
                          ? <RefreshIcon className={'progress-icon'} cursor={'pointer'} onClick={handleProgressRefresh}/>
                          : <InsertDriveFileIcon className={'progress-icon'} />
                      }
                    </Box>
                    <Box className={'progress-bar-zone'}>
                      <Typography className={'progress-bar-title'}>{message}</Typography>
                      <Box className={'progress-bar-container'}>
                        <Box className={'progress-bar'}>
                          <LinearProgress variant="determinate" {...props} value={progress} color='success' className={'progress'}/>
                        </Box>
                        <Typography className={'progress-value'}>{`${progress}%`}</Typography>
                      </Box>
                    </Box>
                  </Box>
            }
          </CardContent>
        </Card>
      </Box>
      {
        openStatusDialog && orderCounty && props.projectTitle &&
          <DocumentRunnerStatusDialog
            open={openStatusDialog}
            setOpen={setOpenStatusDialog}
            county={orderCounty}
            organizationId={props.projectTitle.createdByOrganizationID}
            config={documentRunnerConfig}/>
      }
    </>
  );
}

export default observer(DocumentRunner);
